import { TABLE_FILTER_OPERATORS } from 'utils/constants';

/** Column key of the Last Inspected column; also the Asset `lastInspected` filter/sort key. */
export const LAST_INSPECTED_KEY = 'lastInspected';
/**
 * Scopes Last Inspected (display, sort and `notInspectedSince`) to these Inspection types. Without
 * it, Last Inspected covers every Inspection type.
 */
const LAST_INSPECTED_TYPE_IDS_KEY = 'lastInspectedInspectionTypeIds';
/** `lastInspected IS NULL OR lastInspected < date`. */
export const NOT_INSPECTED_SINCE_KEY = 'notInspectedSince';

export const LAST_INSPECTED_FILTER_KEYS = [
  LAST_INSPECTED_KEY,
  LAST_INSPECTED_TYPE_IDS_KEY,
  NOT_INSPECTED_SINCE_KEY,
];

export const LAST_INSPECTED_QUICK_PICKS = [
  { token: 'rel:3M', unit: 'month', amount: 3 },
  { token: 'rel:6M', unit: 'month', amount: 6 },
  { token: 'rel:1Y', unit: 'year', amount: 1 },
  { token: 'rel:2Y', unit: 'year', amount: 2 },
] as const;

type LastInspectedSelectedKey = {
  key: string;
  value: string[];
};

export type LastInspectedFilterState = {
  /** Inspection type ids chosen in the filter, or undefined for every Inspection type. */
  inspectionTypeIds?: string[];
  /** `YYYY-MM-DD` or a relative token such as `rel:3M`. */
  notInspectedSince?: string;
};

const isSelectedKey = (value: unknown): value is LastInspectedSelectedKey =>
  !!value &&
  typeof value === 'object' &&
  'key' in value &&
  'value' in value &&
  Array.isArray((value as LastInspectedSelectedKey).value);

export const getLastInspectedFilterState = (selectedKeys: unknown): LastInspectedFilterState => {
  const keys = Array.isArray(selectedKeys) ? selectedKeys.filter(isSelectedKey) : [];
  const typeIds = keys.find(({ key }) => key === LAST_INSPECTED_TYPE_IDS_KEY)?.value;
  const notInspectedSince = keys
    .find(({ key }) => key === NOT_INSPECTED_SINCE_KEY)
    ?.value.find(value => !Object.values(TABLE_FILTER_OPERATORS).includes(value));

  return {
    inspectionTypeIds: typeIds?.length ? typeIds : undefined,
    notInspectedSince: notInspectedSince || undefined,
  };
};

/**
 * No Inspection types means every type, so they get no key. `notInspectedSince` carries a
 * `LESS_THAN` sentinel: without it paging.js sends a lone date as `GREATER_THAN_OR_EQUAL`.
 */
export const buildLastInspectedSelectedKeys = ({
  inspectionTypeIds,
  notInspectedSince,
}: LastInspectedFilterState): LastInspectedSelectedKey[] => {
  const selectedKeys: LastInspectedSelectedKey[] = [];

  if (inspectionTypeIds?.length) {
    selectedKeys.push({ key: LAST_INSPECTED_TYPE_IDS_KEY, value: inspectionTypeIds });
  }

  if (notInspectedSince) {
    selectedKeys.push({
      key: NOT_INSPECTED_SINCE_KEY,
      value: [TABLE_FILTER_OPERATORS.LESS_THAN, notInspectedSince],
    });
  }

  return selectedKeys;
};

export const getLastInspectedTypeIdsFromFilters = (
  filteredInfo: Record<string, unknown> | undefined
): string[] | undefined =>
  getLastInspectedFilterState(filteredInfo?.[LAST_INSPECTED_KEY]).inspectionTypeIds;
